import { Activity, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import DetailPageTemplate from '../../templates/DetailPageTemplate'
import { MuiColorInput } from 'mui-color-input'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const PALETTE_OPTIONS = [
  {
    value: 'rainbow',
    label: 'Rainbow',
    gradient:
      'linear-gradient(to right, hsl(0,100%,50%), hsl(67,100%,50%), hsl(135,100%,50%), hsl(202,100%,50%), hsl(270,100%,50%))',
  },
  {
    value: 'magma',
    label: 'Magma',
    gradient:
      'linear-gradient(to right, #000004, #3b0f70, #8c2981, #de4968, #fcfdbf)',
  },
  {
    value: 'viridis',
    label: 'Viridis',
    gradient: 'linear-gradient(to right, #440154, #31688e, #35b779, #fde725)',
  },
  {
    value: 'plasma',
    label: 'Plasma',
    gradient: 'linear-gradient(to right, #0d0887, #cc4778, #f89540, #f0f921)',
  },
  {
    value: 'spectral',
    label: 'Spectral',
    gradient:
      'linear-gradient(to right, #9e0142, #f46d43, #ffffbf, #66c2a5, #3288bd)',
  },
]

const DEFAULT_PLOT = {
  analysis: '',
  type: 'time-series',
  plotName: '',
  config: {},
  unit: '',
  color: '#AAAAAA',
  colorPalette: 'spectral',
  isMultiline: false,
  xAxisLabel: '',
  yAxisLabel: '',
  active: true,
  excludeBackground: false,
}

const EditPlotPage = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const isCreate = !uuid

  const { setBreadcrumbs } = usePageTitle()
  const [plot, setPlot] = useState(DEFAULT_PLOT)
  const [analyses, setAnalyses] = useState([])
  const [analysesFields, setAnalysesFields] = useState({})
  const [analysesHistogramFields, setAnalysesHistogramFields] = useState({})
  const [loading, setLoading] = useState(!isCreate)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchWithAuth('analyses').then(({ data }) => {
      if (data?.data) {
        const list = data.data.map((a) => ({
          ...a,
          uuid: a.uuid || a._id,
        }))
        setAnalyses(list)
        const fields = {}
        const histFields = {}
        list.forEach((a) => {
          fields[a.uuid] = a.timeseries_parameters || {}
          histFields[a.uuid] = a.histogram_parameters || {}
        })
        setAnalysesFields(fields)
        setAnalysesHistogramFields(histFields)
      }
    })
  }, [])

  useEffect(() => {
    if (isCreate) return
    fetchWithAuth(`plots/${uuid}`)
      .then(({ data }) => {
        const plotData = data?.data ?? data
        if (plotData) {
          setPlot({ ...DEFAULT_PLOT, ...plotData })
          setBreadcrumbs(['plot-config', plotData.plotName || 'Plot', 'edit'])
        }
      })
      .catch((err) => console.error('Error fetching plot:', err))
      .finally(() => setLoading(false))
    return () => setBreadcrumbs([])
  }, [uuid, isCreate, setBreadcrumbs])

  const handleChange = (field, value) => {
    if (field === 'type') {
      setPlot((prev) => ({ ...prev, type: value, config: {} }))
    } else if (field.startsWith('config.')) {
      const configField = field.split('.')[1]
      setPlot((prev) => ({
        ...prev,
        config: { ...prev.config, [configField]: value },
      }))
    } else {
      setPlot((prev) => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const url = isCreate ? 'plots' : `plots/${uuid}`
      const method = isCreate ? 'POST' : 'PUT'
      // eslint-disable-next-line no-unused-vars
      const {
        uuid: _uuid,
        analysisName: _analysisName,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        ...payload
      } = plot
      const { data } = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const savedUuid = data?.data?.uuid ?? uuid

      if (!isCreate && savedUuid) {
        const stored = JSON.parse(localStorage.getItem('selectedPlots'))
        if (Array.isArray(stored)) {
          const idx = stored.findIndex((p) => p.uuid === savedUuid)
          if (idx !== -1) {
            const updated = [...stored]
            updated[idx] = { ...stored[idx], ...plot }
            localStorage.setItem('selectedPlots', JSON.stringify(updated))
          }
        }
      }

      navigate(`/plot-config/${savedUuid}`)
    } catch (error) {
      console.error('Error saving plot:', error)
    } finally {
      setSaving(false)
    }
  }

  const tsFields = analysesFields[plot.analysis] || {}
  const histFields = analysesHistogramFields[plot.analysis] || {}

  const formContent = (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={3}>
          {/* Plot Name */}
          <TextField
            fullWidth
            label="Plot Name"
            value={plot.plotName}
            onChange={(e) => handleChange('plotName', e.target.value)}
          />

          {/* Analysis */}
          <FormControl fullWidth variant="outlined">
            <InputLabel id="analysis-label" htmlFor="analysis">
              Analysis
            </InputLabel>
            <Select
              labelId="analysis-label"
              value={plot.analysis}
              onChange={(e) => handleChange('analysis', e.target.value)}
              label="Analysis"
              inputProps={{ id: 'analysis' }}
            >
              {analyses.map((a) => (
                <MenuItem key={a.uuid} value={a.uuid}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Type */}
          <FormControl fullWidth variant="outlined">
            <InputLabel id="type-label" htmlFor="type">
              Type
            </InputLabel>
            <Select
              labelId="type-label"
              value={plot.type}
              onChange={(e) => handleChange('type', e.target.value)}
              label="Type"
              inputProps={{ id: 'type' }}
            >
              <MenuItem value="time-series">Time Series</MenuItem>
              <MenuItem value="histogram">Histogram</MenuItem>
            </Select>
          </FormControl>

          {/* Unit */}
          <TextField
            fullWidth
            label="Unit"
            value={plot.unit}
            onChange={(e) => handleChange('unit', e.target.value)}
          />

          {/* Active */}
          <FormControlLabel
            control={
              <Switch
                checked={plot.active ?? true}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
            }
            label="Active"
          />

          {/* Exclude Background (non-histogram only) */}
          <Activity mode={plot.type !== 'histogram' ? 'visible' : 'hidden'}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={plot.excludeBackground ?? true}
                  onChange={(e) =>
                    handleChange('excludeBackground', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Exclude background"
            />
          </Activity>

          {/* Time-series: multiline toggle */}
          <Activity mode={plot.type === 'time-series' ? 'visible' : 'hidden'}>
            <FormControlLabel
              control={
                <Switch
                  checked={plot.isMultiline || false}
                  onChange={(e) =>
                    handleChange('isMultiline', e.target.checked)
                  }
                />
              }
              label="Multiline"
            />
          </Activity>

          {/* Color / Color Palette */}
          {plot.isMultiline && plot.type === 'time-series' ? (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="color-palette-label" htmlFor="color-palette">
                Color Palette
              </InputLabel>
              <Select
                labelId="color-palette-label"
                value={plot.colorPalette || 'spectral'}
                onChange={(e) => handleChange('colorPalette', e.target.value)}
                label="Color Palette"
                inputProps={{ id: 'color-palette' }}
                renderValue={(val) => {
                  const p = PALETTE_OPTIONS.find((o) => o.value === val)
                  return p ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 12,
                          borderRadius: 0.5,
                          background: p.gradient,
                          flexShrink: 0,
                        }}
                      />
                      {p.label}
                    </Box>
                  ) : (
                    val
                  )
                }}
              >
                {PALETTE_OPTIONS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 12,
                          borderRadius: 0.5,
                          background: p.gradient,
                          flexShrink: 0,
                        }}
                      />
                      {p.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <MuiColorInput
              label="Color"
              value={plot.color}
              onChange={(color) => handleChange('color', color)}
              fullWidth
            />
          )}

          {/* Time-series: parameter(s) */}
          <Activity mode={plot.type === 'time-series' ? 'visible' : 'hidden'}>
            {plot.isMultiline ? (
              <FormControl fullWidth variant="outlined">
                <InputLabel id="parameters-label" htmlFor="parameters">
                  Parameters
                </InputLabel>
                <Select
                  labelId="parameters-label"
                  multiple
                  value={plot.config.parameters || []}
                  onChange={(e) =>
                    handleChange('config.parameters', e.target.value)
                  }
                  inputProps={{ id: 'parameters' }}
                  input={<OutlinedInput label="Parameters" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((key) => (
                        <Chip
                          key={key}
                          label={tsFields[key] || key}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                  disabled={!Object.keys(tsFields).length}
                >
                  {Object.entries(tsFields).map(([key, label]) => {
                    const selected = (plot.config.parameters || []).includes(
                      key,
                    )
                    const atLimit = (plot.config.parameters || []).length >= 10
                    return (
                      <MenuItem
                        key={key}
                        value={key}
                        disabled={!selected && atLimit}
                      >
                        <Checkbox checked={selected} />
                        <ListItemText primary={label || key} />
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth variant="outlined">
                <InputLabel id="ts-parameter-label" htmlFor="ts-parameter">
                  Parameter
                </InputLabel>
                <Select
                  labelId="ts-parameter-label"
                  value={plot.config.parameters?.[0] || ''}
                  onChange={(e) =>
                    handleChange('config.parameters', [e.target.value])
                  }
                  label="Parameter"
                  inputProps={{ id: 'ts-parameter' }}
                  disabled={!Object.keys(tsFields).length}
                >
                  {Object.entries(tsFields).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label || key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Activity>

          {/* Histogram: parameter */}
          <Activity mode={plot.type === 'histogram' ? 'visible' : 'hidden'}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="hist-parameter-label" htmlFor="hist-parameter">
                Parameter
              </InputLabel>
              <Select
                labelId="hist-parameter-label"
                value={plot.config.parameters?.[0] || ''}
                onChange={(e) =>
                  handleChange('config.parameters', [e.target.value])
                }
                label="Parameter"
                inputProps={{ id: 'hist-parameter' }}
                disabled={!Object.keys(histFields).length}
              >
                {Object.entries(histFields).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label || key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Activity>
        </Stack>
      </CardContent>
    </Card>
  )

  return (
    <DetailPageTemplate
      title={isCreate ? 'New Plot' : `Edit: ${plot.plotName || 'Plot'}`}
      isLoading={loading}
      actions={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            component={Link}
            to={isCreate ? '/plot-config' : `/plot-config/${uuid}`}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      }
      sections={[{ content: formContent }]}
    />
  )
}

export default EditPlotPage
